import type { Issue } from './types';
import type { View } from './store';

/**
 * What the command palette offers for what somebody has typed.
 *
 * Kept apart from the screen so the ranking can be tested without a browser.
 * Ranking is the whole feature: a palette that finds the right thing third is
 * one people stop opening, because the keyboard is only faster than the mouse
 * when the first result is the one you meant.
 */

export type Command =
  | { kind: 'view'; id: View; label: string; hint: string }
  | { kind: 'action'; id: 'new'; label: string; hint: string }
  | { kind: 'issue'; id: string; label: string; hint: string; ref: string }
  | { kind: 'run'; id: string; label: string; hint: string }
  // A "how do I…" answer. `view`/`reveal` take the person to the real control
  // (the shared drillTo/revealId reveal); `answer` is the example-first next step
  // shown on arrival. Built by the screen from the help corpus (src/help.ts) so
  // the ranking here stays a pure, tested thing. See design rule P9.
  | { kind: 'help'; id: string; label: string; hint: string; view: View; reveal?: string; answer: string };

export interface Places {
  view: View;
  label: string;
}

/**
 * The verbs the palette can run, kept apart from the screens.
 *
 *   context — actions on the thing you are looking at (the open card): set its
 *     status, its priority. Shown even on an empty query, because with a card
 *     open they are the most likely reason you opened the palette.
 *   global  — actions that stand alone (appearance, sign out). Typed-only, so an
 *     empty palette stays a short, familiar list of places rather than a menu.
 *
 * Both are plain data the SCREEN builds and runs; the ranking here never needs
 * to know what a verb does, only how to find it — by its word OR its category,
 * so "done" and "status" both reach "Set status: Done".
 */
export interface Verbs {
  context: Command[];
  global: Command[];
}

/** Somebody typing a reference means that issue and nothing else. */
const REF = /^#?(\d+[a-z-]*)$/i;

const norm = (s: string) => s.trim().toLowerCase();

/**
 * @param query      what has been typed
 * @param places     the screens this person can reach
 * @param issues     everything on the board
 * @param canCreate  whether raising an issue is allowed here
 */
export function commandsFor(
  query: string,
  places: Places[],
  issues: Issue[],
  canCreate: boolean,
  verbs: Verbs = { context: [], global: [] },
  // The "how do I…" answers matching the query, already ranked by the screen
  // (src/help.ts howTosFor → help Commands). Default empty so existing callers and
  // tests are unaffected. Only surfaced on a typed query — an empty ⌘K stays a
  // short list of places, and the dedicated Help screen is where an empty query
  // shows the whole index.
  helps: Command[] = []
): Command[] {
  const q = norm(query);

  const views: Command[] = places.map((p) => ({
    kind: 'view',
    id: p.view,
    label: p.label,
    hint: 'Go to',
  }));
  const actions: Command[] = canCreate
    ? [{ kind: 'action', id: 'new', label: 'New issue', hint: 'Create' }]
    : [];

  const asIssue = (i: Issue): Command => ({
    kind: 'issue',
    id: i.id,
    label: i.title,
    hint: i.ref,
    ref: i.ref,
  });

  // Nothing typed: the open card's own actions first (the reason you opened it),
  // then the places, then how to start something. No issues, because an
  // unfiltered list of everything is not a suggestion; no global verbs, so an
  // empty palette stays a short list of where to go, not a menu of everything.
  if (!q) return [...verbs.context, ...views, ...actions];

  /*
   * A reference is exact and beats everything.
   *
   * Typing "205" or "#205" means one card. Ranking it among title matches would
   * put "#205" below any issue whose title happens to contain "205", which is
   * the opposite of what was asked for.
   */
  const m = q.match(REF);
  if (m) {
    const wanted = `#${m[1]}`.toLowerCase();
    const exact = issues.filter((i) => norm(i.ref) === wanted);
    const rest = issues.filter((i) => norm(i.ref) !== wanted && norm(i.ref).includes(m[1]));
    if (exact.length || rest.length) return [...exact, ...rest].slice(0, 12).map(asIssue);
  }

  const hit = (s: string) => norm(s).includes(q);
  const startsWith = (s: string) => norm(s).startsWith(q);

  // A name you have half-typed should come before one that merely contains it:
  // "b" is Backlog and Board, not "Something about b".
  const matchedViews = [
    ...views.filter((v) => startsWith(v.label)),
    ...views.filter((v) => !startsWith(v.label) && hit(v.label)),
  ];
  const matchedActions = actions.filter((a) => hit(a.label));

  // A verb is found by its word OR its category — "done" and "status" both reach
  // "Set status: Done" — with an exact word-start first.
  const allVerbs = [...verbs.context, ...verbs.global];
  const verbMatch = (c: Command) => hit(c.label) || hit(c.hint);
  const matchedVerbs = [
    ...allVerbs.filter((c) => startsWith(c.label)),
    ...allVerbs.filter((c) => !startsWith(c.label) && verbMatch(c)),
  ];

  const matchedIssues = [
    ...issues.filter((i) => startsWith(i.title)),
    ...issues.filter((i) => !startsWith(i.title) && hit(i.title)),
  ]
    .slice(0, 12)
    .map(asIssue);

  /*
   * Order: a named screen first, then verbs, then how to start, then cards.
   *
   * Navigation is the commonest reason to open this, and a single letter should
   * still jump to a screen — "b" is Board, not "set status to Backlog", even with
   * a card open. So a view you have half-typed leads. A "how do I…" answer comes
   * next — a person asking "how do I sign off" wants the help, not a card whose
   * title says "sign off" — so help beats verbs and cards but never outranks the
   * screen you were plainly naming. A verb still beats a CARD that merely contains
   * the same word ("done" runs Set status: Done before it opens a card with "done"
   * in its title), which is what verbs-before-issues is for.
   */
  return [...matchedViews, ...helps, ...matchedVerbs, ...matchedActions, ...matchedIssues];
}
